import React, { Fragment, useState } from 'react';
import { animateScroll } from 'react-scroll';
import { Formik } from 'formik';

import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import JobVaultSubmissionForm from 'buyside/components/presentation/JobVaultSubmission/JobVaultSubmissionForm/JobVaultSubmissionForm';
import Banner from 'buyside/components/presentation/Banner/banner';
import {
  getInitialFormValues,
  generateValidationSchema
} from 'buyside/components/presentation/JobVaultSubmission/JobVaultSubmissionHelpers';
import { jobVaultPostingUrl } from 'buyside/util/urlHelper';
import { useIsMobile } from 'buyside/util/hooks/useIsMobile';
import isLocalStorageNameSupported from 'buyside/util/isLocalStorageNameSupported';

import styles from './JobVaultSubmissionPageStyles';

const JobVaultSubmissionPage = ({ createJobPosting, loading, error, currentUser }) => {
  const [errors, setErrors] = useState(false);
  const isMobile = useIsMobile();

  const handleSubmit = (args, actions) => {
    const {
      employerDetails,
      companyInvestmentMandate,
      useCompanyInvestmentMandate,
      positionInvestmentMandate,
      minimum,
      coverLetter,
      positionDetails
    } = args;

    const variables = {
      aum: employerDetails.aum,
      fund_type: employerDetails.fundType,
      strategy: employerDetails.strategy,
      employer_city: employerDetails.city,
      employer_description: employerDetails.description,
      asset_class_names: companyInvestmentMandate.assets,
      sector_names: companyInvestmentMandate.sectors,
      geography_names: companyInvestmentMandate.regions,
      title: positionDetails.title,
      description: positionDetails.description,
      years_of_experience: [minimum.yearsOfExperienceMin, minimum.yearsOfExperienceMax].join('_'),
      minimum_gpa: minimum.gpa.score ? String(minimum.gpa.score) : null,
      minimum_sat: minimum.sat.score ? Number(minimum.sat.score) : null,
      use_company_investment_mandate: useCompanyInvestmentMandate,
      max_gpa: String(minimum.gpa.max),
      max_sat: String(minimum.sat.max),
      act_score: minimum.act.score ? Number(minimum.act.score) : null,
      cover_letter: coverLetter,
      position_city: positionDetails.city,
      firm_experience: minimum.firmExperience,
      degrees_and_certifications: minimum.degreesAndCertifications,
      position_asset_class_names: positionInvestmentMandate.assets,
      position_sector_names: positionInvestmentMandate.sectors,
      position_geography_names: positionInvestmentMandate.regions
    };

    createJobPosting(variables)
      .then(
        ({
          data: {
            createJobPosting: { jobPost }
          }
        }) => {
          if (isLocalStorageNameSupported()) {
            if (jobPost.isActive)
              window.localStorage.setItem('jobVaultFlash', 'Your job posting is now active.');
          }
          window.location.assign(jobVaultPostingUrl(jobPost.id));
        }
      )
      .catch(() => {
        setErrors(true);
        actions.setSubmitting(false);
        animateScroll.scrollToTop({ duration: 1000 });
      });
  };

  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  if (error) throw error;

  const initialValues = getInitialFormValues(currentUser);
  const jobVaultSubmissionSchema = generateValidationSchema(currentUser);

  return (
    <Fragment>
      {errors && (
        <Banner error>
          There was a problem saving your job posting. Please try again at a later time. If the
          problem persists, contact support@sumzero.com for assistance.
        </Banner>
      )}

      <h1 className={styles.header}>Submit a Job</h1>

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={jobVaultSubmissionSchema}
        render={formikProps => (
          <JobVaultSubmissionForm isMobile={isMobile} currentUser={currentUser} {...formikProps} />
        )}
      />
    </Fragment>
  );
};

export default JobVaultSubmissionPage;
